import { axiosReq } from "@/lib/axios"
import { queryClient } from "@/lib/react-query" 
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns"
import { toast } from "sonner";
import { ScheduledPostType } from "../types";
import { captureApiError } from "@/lib/sentry-fn";

export type SocialPostType = {
  event_id: number,
  event: string;
  post_type: "Campaign" | "Post",
  priority: "High" | "Low",
  start_date: string,
  end_date: string,
  pending_posts_count: number,
  content: {
    post_id: number,
    post_content: string,
    post_date: string,
    post_time: string,
    tags: string[],
    image_video_recommendation: string
  }[]
}

export type SchedulePostDataType = {
  date: "2024-12-30",
  posts: {
    event_id: number,
    post_id: number,
    post_content: string,
    post_time: string,
    post_type: "Campaign" | "Post",
  }[]
}[]

export type HistoryPostType = {
  username: string,
  content: string,
  img_link: string,
  hastags: string,
  retweets: number,
  likes: number,
  tweet_url: string,
  date_of_post: string,
}

export type ScheduledPostResponse = {
  date: string,
  posts: ScheduledPostType[]
}

export const useGetScheduledPostV2 = (end_date: string) => {

  const request = async (): Promise<{ data: ScheduledPostResponse[] }> => {

    try {

      const response = await axiosReq.post('/api/approval/get-scheduled-posts-v2/', { end_date })
      return response.data

    } catch (error) {
      captureApiError(error, {
        component: "useGetScheduledPostV2",
        url: `${import.meta.env.VITE_API_URL}/api/calendar/add-calendar/`,
        httpMethod: "POST",
        apiEndpoint: "api/calendar/add-calendar/"
      })

      throw error
    }
  }

  const { data: scheduledPosts, isLoading } = useQuery({
    queryKey: ["schedule_posts", end_date],
    queryFn: request
  });

  return { scheduledPosts, isLoading }
}

export const useGetScheduledPosts = (start_date: Date, end_date: Date) => {

  const request = async (start_date: Date, end_date: Date): Promise<
    {
      message: string,
      data: SocialPostType[],
    }> => {
    const response = await axiosReq.post('/api/approval/get-scheduled-posts/',
      {
        user_id: 2,
        start_date: format(start_date, 'yyyy-MM-dd'),
        end_date: format(end_date, 'yyyy-MM-dd')
      });
    return response.data;
  }

  const { data: scheduledPosts, isLoading } = useQuery({
    queryKey: ["socials-scheduled", start_date, end_date],
    queryFn: () => request(start_date, end_date)
  });

  return { scheduledPosts, isLoading }
}


export const useGetSocialPosts = () => {

  const request = async (): Promise<
    {
      message: string,
      data: SocialPostType[],
    }> => {
    const response = await axiosReq.post('/api/approval/get-pending-posts/',
      {
        user_id: 2,
      });
    return response.data;
  }

  const { data: socialPosts, isLoading } = useQuery({
    queryKey: ["socials"],
    queryFn: request
  });

  return { socialPosts, isLoading }
}

export const useGetHistoryPost = (start_date: string, end_date: string, event_name: string) => {

  const request = async (): Promise<{
    message: string,
    history: HistoryPostType[]
  }> => {
    const response = await axiosReq.post('/api/approval/get-user-history-post/', { start_date, end_date, event_name })
    return response.data
  }

  const { data: historyData } = useQuery({
    queryKey: ["history", start_date, end_date, event_name],
    queryFn: request
  })

  return { historyData }
}



type UpdatePostBodyArgs = {
  post_id: number;
  post_content: string;
  hashtags: string[];
}

export const useUpdatePostBody = () => {

  const request = async (postBody: UpdatePostBodyArgs) => {
    try {
      const response = await axiosReq.post('/api/approval/post-save/', postBody)
      return response.data;
    } catch (error) {

      captureApiError(error, {
        component: "useUpdatePostBody",
        url: `${import.meta.env.VITE_API_URL}/api/approval/post-save/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/post-save/"
      })

      throw error
    }
  }
  const { mutate: updatePostBody } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      toast.success("Content updated")
    }
  })

  return { updatePostBody }
}

export type EventAttributesType = {
  id: number,
  event_id: number,
  smart_goals: string,
  seasonal_cultural: string,
  content_performance: string,
  event_hashtags: string,
  event_keywords: string,
  event_colors: string,
  event_tone: string,
  event_assets_description: string,
  event_name: string,
}

export type BrandAttributesType = {
  id: number,
  event_name: string,
  event_id: number,
  event_performance: string,
  keywords: string,
  content_pillars: string,
  hashtags: string,
  hooks: string

}



export const useGetSocialAttributes = (eventId: number, enabled: boolean) => {
  const request = async (eventId: number): Promise<{
    message: string,
    attribute_events: EventAttributesType,
    attributes_brands: BrandAttributesType
  }> => {
    const response = await axiosReq.get(`/api/approval/get-attribute-brand-event/?event_id=${eventId}`)
    return response.data
  }

  const { data: postAttributes, isLoading } = useQuery({
    queryKey: ["social-attr", eventId],
    queryFn: () => request(eventId),
    enabled: enabled
  })

  return { postAttributes, isLoading }
}

export const useUpdateBrandAttributes = () => {
  const request = async (data: BrandAttributesType) => {
    const response = await axiosReq.post('/api/approval/add-attribute-brands/', data);
    return response.data
  }
  const { mutate: updateBrandAttr } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      toast.message("Attributes saved successfully")
      queryClient.invalidateQueries({ queryKey: ["social-attr"] })
    }
  });

  return { updateBrandAttr }
}


export const useUpdateEventAttributes = () => {
  const request = async (data: EventAttributesType) => {
    const response = await axiosReq.post('/api/approval/add-attribute-events/', data);
    return response.data
  }
  const { mutate: updateEventAttr } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      toast.message("Attributes saved successfully")
      queryClient.invalidateQueries({ queryKey: ["social-attr"] })
    }
  });

  return { updateEventAttr }
}


export const useRegenerateContent = () => {

  const request = async (postId: number): Promise<{
    message: string,
    post: {
      post_id: number;
      post_content: string;
      post_date: string;
      tags: string[],
      image_video_recommendation: string,
    },
  }> => {
    try {
      const response = await axiosReq.post('/api/approval/regenerate_posts/', {
        post_id: postId
      });
      return response.data
    } catch (error) {
      captureApiError(error, {
        apiEndpoint: '/api/approval/regenerate_posts/',
        httpMethod: 'POST',
        component: 'useRegenerateContent',
        url: `${import.meta.env.VITE_API_URL}/api/approval/regenerate_posts/`
      });

      throw error;
    }
  }

  const { mutate: regenerateContent, data: generatedData, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      toast.success("Data generated")
    }
  })

  return { regenerateContent, generatedData, status }
}

export const useSchedulePost = () => {

  const request = async (postId: number) => {
  try{
      const response = await axiosReq.post('/api/approval/schedule_post/', { post_id: postId });
      return response.data
    }catch(error){

      captureApiError(error, {
        apiEndpoint: '/api/approval/schedule_post/',
        httpMethod: 'POST',
        component: 'useSchedulePost',
        url: `${import.meta.env.VITE_API_URL}/api/approval/schedule_post/`
      });

      throw error;
    }
  }

  const { mutate: schedulePost } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      toast.success("Event Scheduled successfully")
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials"] })
      queryClient.invalidateQueries({ queryKey: ["timeline"] })
    }
  })

  return { schedulePost }
}

export const useDiscardEvent = () => {

  const request = async (id: number) => {
  try{
      const response = await axiosReq.post('/api/calendar/discard-recommendation/', {
        event_id: id,
      })

      return response.data
    } catch(error){

      captureApiError(error, {
        apiEndpoint: '/api/calendar/discard-recommendation/',
        httpMethod: 'POST',
        component: 'useDiscardEvent',
        url: `${import.meta.env.VITE_API_URL}/api/calendar/discard-recommendation/`
      });

      throw error;
    }
  }
  const { mutate: discardEvent, status } = useMutation({
    mutationFn: (id: number) => request(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] })
      toast.message("Event removed from suggestions")
    }
  })

  return { discardEvent, status }
}

export const useRegenerateEvent = (setLoading: () => void) => {

  const request = async ({ id, prompt }: { id: number, prompt: string }): Promise<{
    message: string,
    post: {
      post_id: number,
      platform: string,
      text: string,
      image_video_recommendations: string,
      hashtags: string[],
      cta: string[],
      date_of_posting: string,
      time_of_posting: string,
    }[]
  }> => {
  try{
      const response = await axiosReq.post('/api/approval/regenerate_posts/', {
        event_id: id,
        recommendation: prompt
      })
      return response.data
    } catch(error){

      captureApiError(error, {
        apiEndpoint: '/api/approval/regenerate_posts/',
        httpMethod: 'POST',
        component: 'useRegenerateEvent',
        url: `${import.meta.env.VITE_API_URL}/api/approval/regenerate_posts/`
      });

      throw error;
    }
  }


  const { mutate: regenerateEvent, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] })
      setLoading()
    }
  })

  return { regenerateEvent, status }
}

export const useDiscardPost = () => {

  const request = async (id: number) => {
  try{
      const response = await axiosReq.post('/api/approval/discard-post/', { post_id: id });
      return response.data;
    } catch(error){

      captureApiError(error, {
        apiEndpoint: '/api/approval/discard-post/',
        httpMethod: 'POST',
        component: 'useDiscardPost',
        url: `${import.meta.env.VITE_API_URL}/api/approval/discard-post/`
      });

      throw error;
    }
  }

  const { mutate: discardPost, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      toast.message("Post discarded")
    }
  })

  return { discardPost, status }
}

